import { useEffect, useMemo } from 'react';

import { useCartActions, useCartValues } from '@fhs/cart/src/state/cart-store';
import { IBackendCartEntries } from '@rbi-ctg/menu';
import {
  ClickEventComponentNames,
  CustomEventNames,
  EventTypes,
  useCRMEventsContext,
} from 'state/crm-events';

type useDonationProps = {
  donationItems: IBackendCartEntries[];
};

function useDonation({ donationItems }: useDonationProps) {
  const { cartEntries } = useCartValues();
  const { addItemToCart, removeItemFromCart } = useCartActions();
  const { logRBIEvent } = useCRMEventsContext();

  const donationItemSanityIdSet = useMemo(
    () => new Set(donationItems.map(item => item.sanityId)),
    [donationItems]
  );
  const selectedDonationCartEntry = useMemo(() => {
    return cartEntries?.find(
      entry => entry.isDonation && donationItemSanityIdSet.has(entry.sanityId)
    );
  }, [cartEntries, donationItemSanityIdSet]);

  useEffect(() => {
    // Clean up cart entries that are donations but not in the list of donation items.
    cartEntries.forEach(entry => {
      if (entry.isDonation && !donationItemSanityIdSet.has(entry.sanityId)) {
        removeItemFromCart({ cartId: entry.cartId });
      }
    });
  }, [cartEntries, donationItemSanityIdSet, removeItemFromCart]);

  function handleAddDonationCartEntry(donationItem: IBackendCartEntries) {
    if (selectedDonationCartEntry?.sanityId === donationItem.sanityId) {
      return;
    }

    if (selectedDonationCartEntry) {
      removeItemFromCart({ cartId: selectedDonationCartEntry.cartId });
    }

    addItemToCart({ ...donationItem, children: [], isDonation: true });

    logRBIEvent({
      name: CustomEventNames.CLICK_EVENT,
      type: EventTypes.Other,
      attributes: {
        component: ClickEventComponentNames.DONATION,
        text: `Add Donation - ${donationItem.name || 'Unknown'}`,
        componentId: donationItem.sanityId,
        action: 'add',
      },
    });
  }

  function handleRemoveDonationCartEntry({ cartId }: { cartId: string }) {
    const donationToRemove = selectedDonationCartEntry;
    removeItemFromCart({ cartId });

    if (donationToRemove) {
      logRBIEvent({
        name: CustomEventNames.CLICK_EVENT,
        type: EventTypes.Other,
        attributes: {
          component: ClickEventComponentNames.DONATION,
          text: `Remove Donation - ${donationToRemove.name || 'Unknown'}`,
          componentId: donationToRemove.sanityId,
          action: 'remove',
        },
      });
    }
  }

  return {
    selectedDonationCartEntry,
    handleAddDonationCartEntry,
    handleRemoveDonationCartEntry,
  };
}

export default useDonation;
